import { FC, useState } from 'react';

import styled from '@emotion/styled';
import { generatePath, useNavigate } from 'react-router-dom';
import { Exam } from 'store/my-registrations/types';
import { ExamCardParameter, ExamCardValuesList } from 'components';
import { ROUTES } from 'core';
import { useGetOrganisationCountryConfigByDistrictIdQuery } from 'store/my-registrations/myRegistrationsAPI';
import { Tooltip as TooltipBase } from 'react-tooltip';

import { Divider, LinkButton, Quote } from '@britishcouncil/react-solas-ors3';
import { Button } from '@britishcouncil/react-solas';

import { ExamCancelModal } from './ExamCancelModal';
import { ExamCancelInfoModal } from './ExamCancelInfoModal';
import { getTaxInfo } from 'pages/helper/taxInfo';
import { useGetOnlinePaymentDiscountQuery } from 'core/hooks/useGetOnlinePaymentDiscountQuery';

const Tooltip = styled(TooltipBase)`
  max-width: 500px;
  border-radius: 8px;
  background: #000;
  font-size: 12px;
  opacity: 1;
`;

export const ExamItem: FC<{ exam: Exam }> = ({ exam }) => {
  const navigate = useNavigate();
  const [showCancelModal, setCancelModal] = useState(false);
  const [showCancelInfoModal, setCancelInfoModal] = useState(false);
  const { data: districtConfig } = useGetOrganisationCountryConfigByDistrictIdQuery(exam.districtId);
  const { discountPercentage } = useGetOnlinePaymentDiscountQuery(exam.countryId);

  const goToPayment = () => {
    navigate(generatePath(ROUTES.SELECT_PAYMENT, { referenceNumber: exam.registrationGroupReference }));
  };

  const allowAmendments = (districtConfig && districtConfig.allowAmendments) ?? false;
  const showPriceWithDiscount =
    exam.paid && exam.fee < exam.offlinePaymentFee && exam.fee > 0 && exam.offlinePaymentFee > 0;

  const taxInfo = getTaxInfo(exam.taxRate, exam.fee);

  const formatPrice = (fee: number) => `${fee.toFixed(2)} ${exam.currencyCode}`;

  return (
    <>
      <Divider />
      <ExamCardHeader>
        {exam.syllabusName} - {exam.subjectName}
        {!exam.isCanceled && !exam.paid && (
          <Button
            size="xs"
            intent="primary"
            disabled={exam.sessionStageChanged || !exam.isCurrentSession}
            onClick={goToPayment}
          >
            Pay
          </Button>
        )}
      </ExamCardHeader>

      <ExamCardValuesList>
        <tbody>
          <ExamCardParameter label={'Level:'} value={exam.qualificationLevelName} />
          <ExamCardParameter label={'Code:'} value={exam.optionCode} />
          <ExamCardParameter label={'Board:'} value={exam.awardingBody} />
          <ExamCardParameter label={'Location:'} value={exam.districtName} />
          {exam.isGroupAward && (
            <>
              <ExamCardParameter label={'Retake:'} value={exam.isRetake ? 'Yes' : 'No'} />
              <ExamCardParameter label={'Carry fwd.:'} value={exam.isCarryForward ? 'Yes' : 'No'} />
            </>
          )}
          {(exam.isRetake || exam.isCarryForward || exam.isGroupAward) && (
            <>
              <ExamCardParameter label={'Prev. centre number:'} value={exam.previousCentreNumber} />
              <ExamCardParameter label={'Prev. candidate number:'} value={exam.previousCandidateNumber} />
            </>
          )}
          {exam.paid && <ExamCardParameter label={'Payment method:'} value={exam.paymentName} />}
          {!showPriceWithDiscount && (
            <ExamCardParameter
              label={'Price:'}
              value={
                <span>
                  {formatPrice(exam.fee)} {taxInfo}
                </span>
              }
            />
          )}
          {showPriceWithDiscount && (
            <>
              <Tooltip
                id={`price-tooltip-${exam.id}`}
                place="right"
                content={`${discountPercentage} incentive rate applied`}
              />
              <ExamCardParameter
                label={'Price:'}
                value={
                  <span data-tooltip-id={`price-tooltip-${exam.id}`}>
                    <s>{formatPrice(exam.offlinePaymentFee)}</s> {formatPrice(exam.fee)} {taxInfo}
                  </span>
                }
              />
            </>
          )}
          <ExamCardParameter label={'Session:'} value={exam.sessionName} />
        </tbody>
      </ExamCardValuesList>
      <br />
      {!exam.isCanceled && (
        <CancelButtons className="btn-group">
          <Button
            size="xs"
            intent={exam.isCurrentSession ? 'danger' : 'default'}
            disabled={!exam.isCurrentSession || !allowAmendments || exam.paid}
            onClick={() => setCancelModal(true)}
            data-tooltip-id={allowAmendments && exam.paid ? `cancel-btn-tooltip-${exam.id}` : ''}
          >
            Cancel
          </Button>

          <Tooltip
            id={`cancel-btn-tooltip-${exam.id}`}
            place="right"
            content={
              'Please note that the cancellation of registrations is not possible as the registrations have already been marked as paid. Please contact your British Council centre if need any clarifications.'
            }
          />

          {!exam.isCurrentSession && (
            <Button size="xs" intent="default" onClick={() => setCancelInfoModal(true)}>
              ?
            </Button>
          )}
        </CancelButtons>
      )}

      {exam.isCanceled && (
        <CancelInfo>
          <Quote title="Cancelled" intent="danger">
            See <LinkButton onClick={() => setCancelInfoModal(true)}>cancellation details</LinkButton>
          </Quote>
        </CancelInfo>
      )}

      {showCancelInfoModal && <ExamCancelInfoModal exam={exam} onExit={() => setCancelInfoModal(false)} />}
      {showCancelModal && <ExamCancelModal exam={exam} onExit={() => setCancelModal(false)} />}
    </>
  );
};

const ExamCardHeader = styled.h3`
  margin: 8px 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CancelButtons = styled.div`
  margin-top: 1em;
`;

const CancelInfo = styled.div`
  margin-top: 1em;
`;
